import { AnimatePresence } from "framer-motion";
import { useState } from "react";

import { ClickableElement } from "@/components/ClickableElement";
import { DataMode } from "@/components/flow/shared/Deploy/documentation/types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "@/contexts/FlowContext";
import { IntegrationCodeSnippetModal } from "@/components/flow/shared/Deploy/IntegrationCodeSnippetModal";
import { useDeployContext } from "@/contexts/DeployContext";
import { projectSelector } from "@rflocal/shared";
import { useAuth } from "@/contexts/AuthContext";
import { useSignInAndCreateWorkflow } from "@/hooks/useSignInAndCreateWorkflow";
import { useCurrentWorkflow, useIsTraining } from "@/store";
import { useReviewBatchCount } from "@/hooks/useReviewBatchCount";
import { useImproveModel } from "@/hooks/useImproveModel";
import { getImproveButtonProps } from "@/hooks/improveButtonProps";
import { useFeatureEnabled, Feature } from "@/hooks/useFeatureEnabled";

export const IntegrateSideMenu = () => {
    const { task } = useFlow();
    const { track } = useAnalytics();
    const { workflowCredentials } = useDeployContext();
    const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
    const { user } = useAuth();
    const { signInAndCreateWorkflow, loading: signInLoading } = useSignInAndCreateWorkflow();
    const workflow = useCurrentWorkflow();
    const { count: reviewBatchCount } = useReviewBatchCount();
    const { handleImprove, loading: improveLoading } = useImproveModel();
    const isContinuousImprovementEnabled = useFeatureEnabled(Feature.ContinuousImprovement);
    const isTraining = useIsTraining();

    const handleOpenIntegrationModal = () => {
        setIsIntegrationModalOpen(true);
        track({
            event: AnalyticsFlowEvent.DATA_MODE_SELECTED,
            data: { task, dataMode: DataMode.IMAGE }
        });
    };

    const handleImproveClick = () => {
        track({ event: AnalyticsFlowEvent.IMPROVE_MODEL_CLICKED, data: { step: "deploy" } });
        handleImprove();
    };

    const hasImagesToReview = (reviewBatchCount ?? 0) > 0;
    const highlightImproveButton = !isTraining && (reviewBatchCount ?? 0) >= 10;
    const isImproveDisabled =
        improveLoading || isTraining || (signInLoading && !workflow) || !hasImagesToReview;
    const improveProps = getImproveButtonProps(isTraining, improveLoading, {
        willPublish: false,
        iconStyle: "fad"
    });

    return (
        <AnimatePresence mode="wait">
            <div className="content flex flex-col justify-between">
                <div className="flex flex-col gap-4">
                    <ClickableElement
                        icon="fad fa-code"
                        title="Integrate My Model"
                        description="Run your model calling our Serverless API."
                        onClick={handleOpenIntegrationModal}
                        isSelected={false}
                    />
                    {isContinuousImprovementEnabled && (
                        <ClickableElement
                            icon={improveProps.icon}
                            title={improveProps.title}
                            description={improveProps.description}
                            onClick={handleImproveClick}
                            isSelected={highlightImproveButton}
                            className={
                                highlightImproveButton ? "bg-white hover:bg-tint" : undefined
                            }
                            chip={highlightImproveButton ? "New Data Ready" : ""}
                            disabled={isImproveDisabled}
                        />
                    )}
                    <ClickableElement
                        icon="fad fa-network-wired"
                        title="Add Intelligence & Automation"
                        description="Add complex logic to your model with workflows."
                        onClick={() => {
                            if (!user) {
                                signInAndCreateWorkflow();
                                return;
                            }
                            track({
                                event: AnalyticsFlowEvent.OPEN_WORKFLOWS_BUTTON_CLICKED,
                                data: {
                                    workflowUrl: workflowCredentials.workflowUrl,
                                    workspaceUrl: workflowCredentials.workspaceUrl
                                }
                            });
                            window.open(
                                `${projectSelector.appUrl(false, true)}/${workflowCredentials.workspaceUrl}/workflows/edit/${workflowCredentials.workflowUrl}`,
                                "_blank"
                            );
                        }}
                        isSelected={false}
                    />
                </div>

                <IntegrationCodeSnippetModal
                    isOpen={isIntegrationModalOpen}
                    onClose={() => setIsIntegrationModalOpen(false)}
                />
            </div>
        </AnimatePresence>
    );
};
